import { useState } from "react";
import logoUrl from "@/assets/logo.svg";
import {
  Link,
  NavLink,
  Outlet,
  useMatch,
  useParams,
} from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  KeyRound,
  Package,
  Shield,
  CreditCard,
  GitBranch,
  Activity,
  BarChart2,
  Coins,
  LogOut,
  Settings,
  Settings2,
  UserPlus,
  Plug,
  CircleUser,
  Menu,
  HelpCircle,
  TrendingUp,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { OnboardingChecklist } from "@/components/OnboardingChecklist";
import { OrgSwitcher } from "@/components/OrgSwitcher";
import { ProjectSelector } from "@/components/nav/ProjectSelector";
import { TrialBanner } from "@/components/TrialBanner";
import { MemberAvatar } from "@/components/ui/avatar-initials";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useGettingStartedDismissed } from "@/hooks/useGettingStartedDismissed";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/AuthProvider";

interface NavItem {
  suffix: string;
  labelKey: string;
  icon: LucideIcon;
  adminOnly?: boolean;
  group?: "team" | "settings";
  end?: boolean;
  disabled?: boolean;
}

const ORG_NAV_ITEMS: NavItem[] = [
  { suffix: "", labelKey: "nav.dashboard", icon: LayoutDashboard, end: true },
  { suffix: "/executive", labelKey: "nav.executive", icon: TrendingUp },
  { suffix: "/projects", labelKey: "nav.projects", icon: FolderKanban },
  { suffix: "/cartridges", labelKey: "nav.cartridges", icon: Package },
  { suffix: "/cost", labelKey: "nav.cost", icon: Coins, adminOnly: true },
  {
    suffix: "/members",
    labelKey: "nav.members",
    icon: Users,
    adminOnly: true,
    group: "team",
  },
  {
    suffix: "/api-keys",
    labelKey: "nav.apiKeys",
    icon: KeyRound,
    adminOnly: true,
    group: "team",
  },
  { suffix: "/onboarding", labelKey: "nav.onboarding", icon: UserPlus, group: "settings" },
  {
    suffix: "/integrations",
    labelKey: "nav.integrations",
    icon: Plug,
    adminOnly: true,
    group: "settings",
  },
  { suffix: "/settings", labelKey: "nav.settings", icon: Settings, group: "settings" },
  { suffix: "/profile", labelKey: "nav.myProfile", icon: CircleUser, group: "settings" },
  {
    suffix: "/license",
    labelKey: "nav.license",
    icon: CreditCard,
    adminOnly: true,
    group: "settings",
  },
];

const ORG_NAV_GROUPS = [
  { key: "team" as const, labelKey: "nav.group.team" },
  { key: "settings" as const, labelKey: "nav.group.settings" },
];

const PROJECT_NAV_ITEMS: NavItem[] = [
  {
    suffix: "overview",
    labelKey: "projectPortal.tabs.overview",
    icon: LayoutDashboard,
    end: true,
  },
  { suffix: "pipelines", labelKey: "projectPortal.tabs.pipelines", icon: GitBranch },
  { suffix: "activity", labelKey: "projectPortal.tabs.activity", icon: Activity },
  { suffix: "governance", labelKey: "projectPortal.tabs.governance", icon: Shield },
  { suffix: "metrics", labelKey: "projectPortal.tabs.metrics", icon: BarChart2 },
  { suffix: "config", labelKey: "projectPortal.tabs.config", icon: Settings2 },
];

interface SidebarContentProps {
  onNavigate?: () => void;
}

function SidebarContent({ onNavigate }: SidebarContentProps) {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const isAdmin = useIsAdmin();
  const { dismissed, restore } = useGettingStartedDismissed();
  const base = `/${orgSlug}`;
  const projectMatch = useMatch("/:orgSlug/projects/:projectSlug/*");
  const activeProjectSlug = projectMatch?.params.projectSlug ?? null;

  const navItemClass = (isActive: boolean) =>
    cn(
      "flex items-center gap-3 rounded-md border-l-2 px-3 py-2 text-sm font-medium transition-colors",
      isActive
        ? "border-primary bg-sidebar-accent text-sidebar-accent-foreground"
        : "border-transparent text-sidebar-foreground opacity-60 hover:opacity-100 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
    );

  const renderNavLink = (item: NavItem, to: string) => (
    <NavLink
      key={to}
      to={to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) => navItemClass(isActive)}
    >
      {({ isActive }) => (
        <>
          <item.icon className={cn("h-4 w-4", isActive && "text-primary")} />
          {t(item.labelKey)}
        </>
      )}
    </NavLink>
  );

  // Org-level top items (no group field)
  const orgTopItems = ORG_NAV_ITEMS.filter(
    (i) => !i.group && (!i.adminOnly || isAdmin),
  );

  return (
    <>
      <div className="px-4 pb-2 pt-4">
        <div className="mb-3">
          <Link to={base} onClick={onNavigate}>
            <img src={logoUrl} alt="RaiSE" className="h-9 invert dark:invert-0" />
          </Link>
        </div>
        {user && <OrgSwitcher />}
        {user && orgSlug && (
          <ProjectSelector orgSlug={orgSlug} activeProjectSlug={activeProjectSlug} />
        )}
      </div>
      <div className="mx-4 border-t border-sidebar-border/50" />
      <nav className="flex-1 overflow-y-auto p-2 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-sidebar-border [&::-webkit-scrollbar-track]:bg-transparent">
        {activeProjectSlug ? (
          /* PROJECT MODE — full sidebar swap to project-level nav */
          <TooltipProvider>
            <p className="px-3 pb-1 pt-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              {t("nav.projectSection")}
            </p>
            <div className="space-y-0.5">
              {PROJECT_NAV_ITEMS.map((item) => {
                if (item.disabled) {
                  return (
                    <Tooltip key={item.suffix}>
                      <TooltipTrigger asChild>
                        <span className="flex cursor-not-allowed items-center gap-3 rounded-md border-l-2 border-transparent px-3 py-2 text-sm font-medium text-muted-foreground/50">
                          <item.icon className="h-4 w-4" />
                          {t(item.labelKey)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t("projectPortal.comingSoon")}
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                const to = `${base}/projects/${activeProjectSlug}/${item.suffix}`;
                return renderNavLink(item, to);
              })}
            </div>
          </TooltipProvider>
        ) : (
          /* ORG MODE — ungrouped top block + Team + Settings groups */
          <>
            <div className="space-y-0.5">
              {orgTopItems.map((item) =>
                renderNavLink(item, `${base}${item.suffix}`),
              )}
            </div>
            {ORG_NAV_GROUPS.map(({ key, labelKey }) => {
              const items = ORG_NAV_ITEMS.filter(
                (i) => i.group === key && (!i.adminOnly || isAdmin),
              );
              if (items.length === 0) return null;
              return (
                <div key={key} className="mt-1">
                  <div className="mx-1 my-2 border-t border-sidebar-border/40" />
                  <p className="px-3 pb-1 pt-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t(labelKey)}
                  </p>
                  <div className="space-y-0.5">
                    {items.map((item) =>
                      renderNavLink(item, `${base}${item.suffix}`),
                    )}
                    {key === "settings" && dismissed && (
                      <button
                        onClick={restore}
                        aria-label="Show Getting Started"
                        className={navItemClass(false)}
                      >
                        <HelpCircle className="h-4 w-4" />
                        {t("nav.showGettingStarted")}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </nav>
      {user && (
        <div className="p-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="flex w-full items-center gap-3 rounded-xl bg-sidebar-foreground/5 px-2 py-2 ring-1 ring-sidebar-border/40 backdrop-blur-sm transition-all hover:bg-sidebar-foreground/10 hover:ring-sidebar-border/70">
              <MemberAvatar name={user.name} />
              <div className="min-w-0 flex-1 text-left">
                <p className="truncate text-sm font-medium text-sidebar-foreground">
                  {user.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {user.email}
                </p>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="top" align="start" className="w-56">
              <DropdownMenuItem
                className="cursor-pointer text-destructive focus:text-destructive"
                onClick={() => {
                  logout().then(() => {
                    window.location.href = "/login";
                  });
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t("nav.signOut")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </>
  );
}

export function AppLayout() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const unifiedBackground =
    "from-background via-primary/5 to-primary/10 dark:from-background dark:via-background dark:to-background";

  return (
    <div
      className={cn(
        "flex h-screen bg-gradient-to-r transition-colors duration-300",
        unifiedBackground,
      )}
    >
      {isMobile ? (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent
            side="left"
            className="flex w-64 flex-col gap-0 border-sidebar-border bg-background p-0"
          >
            <SidebarContent onNavigate={() => setSidebarOpen(false)} />
          </SheetContent>
        </Sheet>
      ) : (
        <aside className="flex w-64 flex-col border-r border-sidebar-border bg-transparent">
          <SidebarContent />
        </aside>
      )}
      <main className="flex-1 overflow-auto bg-transparent p-6">
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(true)}
            aria-label={t("nav.openMenu")}
            className="mb-4 rounded-md border border-sidebar-border p-2 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
        <TrialBanner />
        <OnboardingChecklist />
        <Outlet />
      </main>
    </div>
  );
}
